import { useRef, useState } from "react";
import * as XLSX from "xlsx";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Upload, FileSpreadsheet, AlertTriangle, CheckCircle2, Wifi, WifiOff } from "lucide-react";
import { invokeFunction } from "@/integrations/supabase/client";
import type { HubState, LearnerProfile } from "@/lib/learnerHub/types";
import { parseCSV, rowToProfile, talentLMSUserToProfile } from "@/lib/learnerHub/utils";
import { SAMPLE_CSV } from "@/lib/learnerHub/constants";

interface Props {
  state: HubState;
  setHub: (partial: Partial<HubState>) => void;
}

const LMS_CONNECTORS = [
  { name: "TalentLMS", status: "demo" as const },
  { name: "Moodle", status: "soon" as const },
  { name: "Canvas LMS", status: "soon" as const },
  { name: "Cornerstone OnDemand", status: "soon" as const },
  { name: "SAP SuccessFactors", status: "soon" as const },
  { name: "iSpring Learn", status: "soon" as const },
];

async function callHubAI(prompt: string, maxTokens = 1000): Promise<string> {
  const { data, error } = await invokeFunction("hub-assistant", {
    body: { prompt, maxTokens },
  });
  if (error) throw new Error(typeof error === "string" ? error : (error as any).message || "AI call failed");
  return (data as any).text || "";
}

async function mapColumnsWithAI(
  headers: string[],
  sampleRows: Record<string, string>[]
): Promise<{ mapping: Record<string, string>; unmapped: string[] }> {
  const CANONICAL_FIELDS = [
    "name", "employeeId", "email", "site", "role",
    "courseName", "completionDate", "expiryDate", "score", "issuingBody",
  ];
  const prompt = `Map these CSV column headers from a mining training spreadsheet to canonical learner profile fields.

CSV headers: ${JSON.stringify(headers)}
Sample rows (first 3): ${JSON.stringify(sampleRows)}

Canonical fields and their meaning:
- name: full name of the learner
- employeeId: employee or staff ID number
- email: email address of the learner
- site: mine site or facility name
- role: job role or position title
- courseName: name of the training course or certification
- completionDate: date the course was completed (any date format)
- expiryDate: date the certification expires, if applicable
- score: numeric score or grade (0–100)
- issuingBody: organisation that issued the certificate or ran the course

Return ONLY valid JSON with exactly this shape:
{
  "mapping": {
    "CsvColumnName": "canonicalField"
  },
  "unmapped": ["columnsWithNoConfidentMatch"]
}

Only include mappings you are confident about. Put ambiguous or unrecognised columns in unmapped.`;

  const text = await callHubAI(prompt);
  return JSON.parse(text);
}

async function simulateTalentLMSConnection(subdomain: string): Promise<any[]> {
  const prompt = `Generate a realistic TalentLMS REST API /users response for a Chilean copper mining company with subdomain "${subdomain}".

Return ONLY valid JSON in this exact shape:
{
  "users": [
    {
      "id": 1,
      "login": "j.garcia",
      "name": "Jorge García",
      "email": "j.garcia@${subdomain}.cl",
      "role": "Operador de camión minero",
      "custom_field_1": "Mina Norte",
      "completions": [
        {
          "course_name": "Gas Detection Level 1",
          "completion_date": "2024-11-15",
          "completion_percentage": 100,
          "score": 87
        }
      ]
    }
  ]
}

Requirements:
- 5 users total
- Mix of Spanish and English course names (as real mining companies use both)
- Realistic Chilean first and last names
- Roles: use a mix of Operador de camión, Operador jumbo, Técnico mantenimiento, Operadora rajo
- Sites: use a mix of Mina Norte, Mina Atacama, Mina Collahuasi, Mina San José
- 1–3 completions per user
- Include at least one certification with an expiry date in the past (expired)
- custom_field_1 is the mine site`;

  const text = await callHubAI(prompt, 1500);
  const data = JSON.parse(text);
  return data.users || [];
}

function parseCsvFile(text: string) {
  return parseCSV(text);
}

export default function ImportTab({ state, setHub }: Props) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);

  const handleFile = async (file: File) => {
    try {
      let csvText = "";
      if (file.name.endsWith(".csv")) {
        csvText = await file.text();
      } else {
        const buffer = await file.arrayBuffer();
        const wb = XLSX.read(buffer, { type: "array" });
        const ws = wb.Sheets[wb.SheetNames[0]];
        csvText = XLSX.utils.sheet_to_csv(ws);
      }
      const parsed = parseCsvFile(csvText);
      setHub({ csvData: parsed, csvMapping: null });
    } catch {
      setHub({ toast: { message: "Failed to parse file", type: "error" } });
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  };

  const loadSample = () => {
    const parsed = parseCsvFile(SAMPLE_CSV);
    setHub({ csvData: parsed, csvMapping: null });
  };

  const runAIMapping = async () => {
    if (!state.csvData) return;
    setHub({ csvWorking: true });
    try {
      const { headers, rows } = state.csvData;
      const sampleRows = rows.slice(0, 3).map((row) => {
        const obj: Record<string, string> = {};
        headers.forEach((h, i) => { obj[h] = row[i] || ""; });
        return obj;
      });
      const mapping = await mapColumnsWithAI(headers, sampleRows);
      setHub({ csvMapping: mapping });
    } catch (err: any) {
      setHub({ toast: { message: err.message || "AI mapping failed", type: "error" } });
    } finally {
      setHub({ csvWorking: false });
    }
  };

  const importCSV = () => {
    if (!state.csvData || !state.csvMapping) return;
    const { headers, rows } = state.csvData;
    const newProfiles: LearnerProfile[] = rows.map((row, i) =>
      rowToProfile(row, headers, state.csvMapping!.mapping, i)
    );
    setHub({
      learners: [...state.learners, ...newProfiles],
      activeTab: "profiles",
      toast: { message: `Imported ${newProfiles.length} profile(s)`, type: "success" },
    });
  };

  const connectTalentLMS = async () => {
    setHub({ talentWorking: true, talentPreview: null });
    try {
      // Attempt live connection first; CORS will fail in browser, fall through to demo
      let users: any[] = [];
      let demoMode = false;
      try {
        const creds = btoa(`${state.talentKey}:`);
        const res = await fetch(`https://${state.talentDomain}.talentlms.com/api/v1/users`, {
          headers: { Authorization: `Basic ${creds}` },
        });
        const data = await res.json();
        users = Array.isArray(data) ? data : data.users || [];
      } catch {
        demoMode = true;
        users = await simulateTalentLMSConnection(state.talentDomain);
      }
      setHub({
        talentPreview: users,
        talentWorking: false,
        toast: demoMode
          ? { message: "Live connection requires a backend proxy. Running in demo mode.", type: "success" }
          : undefined,
      });
    } catch (err: any) {
      setHub({
        talentWorking: false,
        toast: { message: err.message || "Connection failed", type: "error" },
      });
    }
  };

  const importTalentLMS = () => {
    if (!state.talentPreview) return;
    const newProfiles = state.talentPreview.map(talentLMSUserToProfile);
    setHub({
      learners: [...state.learners, ...newProfiles],
      talentPreview: null,
      activeTab: "profiles",
      toast: { message: `Imported ${newProfiles.length} profile(s) from TalentLMS`, type: "success" },
    });
  };

  return (
    <div className="space-y-6">
      {/* Tier switcher */}
      <div className="flex gap-2">
        <Button
          variant={state.importTier === "tier2" ? "default" : "outline"}
          size="sm"
          onClick={() => setHub({ importTier: "tier2" })}
        >
          <FileSpreadsheet className="h-4 w-4 mr-2" />
          CSV / Excel
        </Button>
        <Button
          variant={state.importTier === "tier1" ? "default" : "outline"}
          size="sm"
          onClick={() => setHub({ importTier: "tier1" })}
        >
          <Wifi className="h-4 w-4 mr-2" />
          LMS connectors
        </Button>
      </div>

      {state.importTier === "tier2" ? (
        <div className="space-y-4">
          {/* Drop zone */}
          <div
            className={`border-2 border-dashed rounded-lg p-10 text-center transition-colors cursor-pointer ${
              dragOver ? "border-primary bg-primary/5" : "border-muted-foreground/30 hover:border-primary/50"
            }`}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <Upload className="h-8 w-8 mx-auto text-muted-foreground mb-3" />
            <p className="text-sm font-medium">Drop a CSV or Excel file here, or click to browse</p>
            <p className="text-xs text-muted-foreground mt-1">.csv, .xlsx, .xls accepted</p>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.xlsx,.xls"
              className="hidden"
              onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])}
            />
          </div>

          <div className="flex items-center gap-2">
            <div className="h-px flex-1 bg-border" />
            <span className="text-xs text-muted-foreground">or</span>
            <div className="h-px flex-1 bg-border" />
          </div>

          <Button variant="outline" size="sm" onClick={loadSample}>
            Load sample CSV
          </Button>

          {/* Parsed preview */}
          {state.csvData && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm">File parsed</CardTitle>
                <CardDescription>
                  {state.csvData.rows.length} rows · {state.csvData.headers.length} columns
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex flex-wrap gap-1.5">
                  {state.csvData.headers.map((h) => (
                    <Badge key={h} variant="secondary" className="text-xs">{h}</Badge>
                  ))}
                </div>
                <Button
                  size="sm"
                  onClick={runAIMapping}
                  disabled={state.csvWorking}
                >
                  {state.csvWorking ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Mapping…</>
                  ) : (
                    "Map columns with AI"
                  )}
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Column mapping result */}
          {state.csvMapping && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm">Column mapping</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="space-y-1.5">
                  {Object.entries(state.csvMapping.mapping).map(([csv, canonical]) => (
                    <div key={csv} className="flex items-center gap-2 text-sm">
                      <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0" />
                      <span className="font-mono text-xs bg-muted px-1.5 py-0.5 rounded">{csv}</span>
                      <span className="text-muted-foreground">→</span>
                      <span className="text-xs font-medium">{canonical}</span>
                    </div>
                  ))}
                  {state.csvMapping.unmapped.map((col) => (
                    <div key={col} className="flex items-center gap-2 text-sm">
                      <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0" />
                      <span className="font-mono text-xs bg-muted px-1.5 py-0.5 rounded">{col}</span>
                      <Badge variant="outline" className="text-xs text-amber-600 border-amber-300">unmapped</Badge>
                    </div>
                  ))}
                </div>
                <Button size="sm" onClick={importCSV}>
                  Import {state.csvData?.rows.length} profiles
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {/* TalentLMS connector */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm">TalentLMS</CardTitle>
                <Badge className="bg-blue-500 text-xs">demo ready</Badge>
              </div>
              <CardDescription>AI-simulated responses with real API schema</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="space-y-2">
                <Label className="text-xs">Subdomain</Label>
                <div className="flex items-center gap-1">
                  <Input
                    placeholder="mycompany"
                    value={state.talentDomain}
                    onChange={(e) => setHub({ talentDomain: e.target.value })}
                    className="h-8 text-sm"
                  />
                  <span className="text-xs text-muted-foreground whitespace-nowrap">.talentlms.com</span>
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-xs">API key</Label>
                <Input
                  type="password"
                  placeholder="••••••••••••"
                  value={state.talentKey}
                  onChange={(e) => setHub({ talentKey: e.target.value })}
                  className="h-8 text-sm"
                />
              </div>
              <Button
                size="sm"
                onClick={connectTalentLMS}
                disabled={!state.talentDomain || state.talentWorking}
              >
                {state.talentWorking ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Connecting…</>
                ) : (
                  "Connect (demo mode)"
                )}
              </Button>
            </CardContent>
          </Card>

          {/* TalentLMS preview */}
          {state.talentPreview && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm">Preview — {state.talentPreview.length} users</CardTitle>
                <CardDescription className="flex items-center gap-1.5">
                  <WifiOff className="h-3.5 w-3.5 text-amber-500" />
                  Live connection requires a backend proxy. Running in demo mode.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <ul className="space-y-1.5 text-sm">
                  {state.talentPreview.map((u) => (
                    <li key={u.id} className="flex items-center gap-2">
                      <span className="font-medium">{u.name}</span>
                      <span className="text-muted-foreground">·</span>
                      <span className="text-muted-foreground text-xs">{u.role}</span>
                      <span className="text-muted-foreground">·</span>
                      <span className="text-muted-foreground text-xs">{u.custom_field_1}</span>
                      <Badge variant="outline" className="text-xs ml-auto">
                        {(u.completions || []).length} course{(u.completions || []).length !== 1 ? "s" : ""}
                      </Badge>
                    </li>
                  ))}
                </ul>
                <Button size="sm" onClick={importTalentLMS}>
                  Import {state.talentPreview.length} profiles
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Coming-soon connector grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {LMS_CONNECTORS.filter((c) => c.status === "soon").map((connector) => (
              <Card key={connector.name} className="opacity-50">
                <CardContent className="pt-4 pb-4 flex items-center justify-between">
                  <span className="text-sm font-medium">{connector.name}</span>
                  <Badge variant="outline" className="text-xs">coming soon</Badge>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
